import {Results, SelfieSegmentation} from '@mediapipe/selfie_segmentation';

// ============================================================================
// Типы
// ============================================================================

/**
 * Опции для эффекта размытия фона
 */
export interface BackgroundBlurOptions {
    /** Радиус размытия в пикселях (от 1 до 50) */
    blurRadius?: number;
    /** Выбор модели сегментации: 0 - general, 1 - landscape (лучше для видео) */
    modelSelection?: 0 | 1;
    /** FPS обработки видео */
    frameRate?: number;
    /** Минимальная уверенность для определения человека (0-1) */
    segmentationThreshold?: number;
    modelRootPath?: string;
}


/**
 * Состояние эффекта
 */
type EffectState = 'idle' | 'initializing' | 'running' | 'stopping' | 'error';

// ============================================================================
// Основной класс
// ============================================================================

export class BackgroundBlurEffect {
    // Публичные свойства
    public state: EffectState = 'idle';
    public onStateChange?: (state: EffectState) => void;
    public onError?: (error: Error) => void;

    // Приватные свойства
    private options: Required<BackgroundBlurOptions>;
    private selfieSegmentation: SelfieSegmentation | null = null;
    private inputVideo: HTMLVideoElement | null = null;
    private outputCanvas: HTMLCanvasElement | null = null;
    private canvasContext: CanvasRenderingContext2D | null = null;
    private outputStream: MediaStream | null = null;
    private tempCanvas: HTMLCanvasElement | null = null;
    private tempContext: CanvasRenderingContext2D | null = null;
    private tempCanvasPadding: number = 100;
    private frameId: number | null = null;
    private originalStream: MediaStream | null = null;

    /**
     * Конструктор
     */
    constructor(options: BackgroundBlurOptions = {}) {
        this.options = {
            modelRootPath: '',
            blurRadius: 15,
            modelSelection: 1,
            frameRate: 30,
            segmentationThreshold: 0.7,
            ...options
        };

        this.validateOptions();
    }

    /**
     * Валидация опций
     */
    private validateOptions(): void {
        if (this.options.blurRadius < 1 || this.options.blurRadius > 50) {
            throw new Error('blurRadius должен быть между 1 и 50');
        }
        if (this.options.frameRate < 1 || this.options.frameRate > 60) {
            throw new Error('frameRate должен быть между 1 и 60');
        }
        if (this.options.segmentationThreshold < 0 || this.options.segmentationThreshold > 1) {
            throw new Error('segmentationThreshold должен быть между 0 и 1');
        }
    }

    /**
     * Создает новый MediaStream с размытым фоном
     */
    async createBlurredStream(originalStream: MediaStream): Promise<MediaStream> {
        try {
            this.setState('initializing');
            this.originalStream = originalStream;

            // 1. Инициализируем Selfie Segmentation
            await this.initSelfieSegmentation();

            // 2. Создаем элементы для обработки видео
            await this.createVideoElements(originalStream);

            // 3. Создаем выходной canvas и stream
            this.outputStream = this.createOutputStream();

            // 4. Запускаем обработку кадров
            await this.startProcessing();

            this.setState('running');
            return this.outputStream;

        } catch (error) {
            this.setState('error');
            const err = error instanceof Error ? error : new Error('Unknown error');
            this.onError?.(err);
            throw err;
        }
    }

    /**
     * Инициализация MediaPipe Selfie Segmentation
     */
    private async initSelfieSegmentation(): Promise<void> {
        this.selfieSegmentation = new SelfieSegmentation({
            locateFile: (file: string): string => {
                return `${this.options.modelRootPath}${file}`;
                //return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
            }
        });

        this.selfieSegmentation.setOptions({
            modelSelection: this.options.modelSelection
        });

        this.selfieSegmentation.onResults((results: Results) => {
            this.processSegmentationResults(results);
        });
    }

    private applyCanvasSize() {
        if (this.inputVideo && this.outputCanvas && this.tempCanvas) {
            const width = this.inputVideo.videoWidth;
            const height = this.inputVideo.videoHeight;

            if (this.outputCanvas.width !== width || this.outputCanvas.height !== height) {
                this.outputCanvas.width = width;
                this.outputCanvas.height = height;
            }

            let expectedWidth = width + this.tempCanvasPadding * 2;
            let expectedHeight = height + this.tempCanvasPadding * 2;

            if (this.tempCanvas.width !== expectedWidth || this.tempCanvas.height !== expectedHeight) {
                this.tempCanvas.width = expectedWidth;
                this.tempCanvas.height = expectedHeight;
            }
        }
    }

    /**
     * Создание видео и canvas элементов
     */
    private createVideoElements(originalStream: MediaStream): Promise<void> {
        return new Promise((resolve, reject) => {
            // Создаем видео элемент для ввода
            this.inputVideo = document.createElement('video');
            this.inputVideo.playsInline = true;
            this.inputVideo.muted = true;
            this.inputVideo.srcObject = originalStream;

            // Создаем canvas для вывода
            this.outputCanvas = document.createElement('canvas');
            this.canvasContext = this.outputCanvas.getContext('2d');

            if (!this.canvasContext) {
                reject(new Error('Не удалось получить контекст canvas'));
                return;
            }

            // Создаем временный canvas для размытия
            this.tempCanvas = document.createElement('canvas');
            this.tempContext = this.tempCanvas.getContext('2d');

            if (!this.tempContext) {
                reject(new Error('Не удалось получить контекст временного canvas'));
                return;
            }


            // Ждем загрузки метаданных видео
            this.inputVideo.onloadedmetadata = (): void => {
                if (!this.inputVideo || !this.outputCanvas || !this.tempCanvas) {
                    reject(new Error('Элементы не инициализированы'));
                    return;
                }
                this.applyCanvasSize();


                resolve();
            };

            this.inputVideo.onerror = (): void => {
                reject(new Error('Ошибка загрузки видео'));
            };

            this.inputVideo.play().catch(reject);
        });
    }

    /**
     * Создание выходного MediaStream
     */
    private createOutputStream(): MediaStream {
        if (!this.outputCanvas) {
            throw new Error('Canvas не инициализирован');
        }

        // Создаем поток из canvas с указанным FPS
        const videoStream = this.outputCanvas.captureStream(this.options.frameRate);

        // Добавляем аудио треки из оригинального потока
        if (this.originalStream) {
            this.originalStream.getAudioTracks().forEach((track: MediaStreamTrack) => {
                try {
                    videoStream.addTrack(track.clone());
                } catch (error) {
                    console.warn('Не удалось добавить аудио трек:', error);
                }
            });
        }

        return videoStream;
    }

    /**
     * Запуск обработки кадров
     */
    private async startProcessing(): Promise<void> {
        if (!this.inputVideo || !this.selfieSegmentation || !this.outputCanvas) {
            throw new Error('Элементы не инициализированы');
        }

        const ctx = this.outputCanvas.getContext('2d');
        if (!ctx) {
            throw new Error('Не удалось получить контекст canvas');
        }

        const FPS = 30;
        const frameInterval = 1000 / FPS; // ~33.3ms
        let lastFrameTime = 0;

        // Функция обработки каждого кадра
        const processFrame = async (timestamp: number): Promise<void> => {
            if (this.state !== 'running' || !this.selfieSegmentation) {
                return;
            }

            // Пропускаем кадр, если не прошло достаточно времени
            if (timestamp - lastFrameTime < frameInterval) {
                requestAnimationFrame(processFrame);
                return;
            }
            lastFrameTime = timestamp;


            try {
                // Пропускаем обработку, если видео не готово
                if (this.inputVideo!.videoWidth === 0 || this.inputVideo!.videoHeight === 0) {
                    requestAnimationFrame(processFrame);
                    return;
                }

                // Устанавливаем размер canvas под видео
                this.outputCanvas!.width = this.inputVideo!.videoWidth;
                this.outputCanvas!.height = this.inputVideo!.videoHeight;

                // Отправляем кадр в selfieSegmentation
                await this.selfieSegmentation.send({image: this.inputVideo!});
            } catch (error) {
                console.error('Ошибка обработки кадра:', error);
            }

            // Продолжаем обработку
            if (this.state === 'running') {
                requestAnimationFrame(processFrame);
            }
        };

        // Запускаем обработку
        this.state = 'running';
        requestAnimationFrame(processFrame);
    }

    /**
     * Обработка результатов сегментации
     */
    private processSegmentationResults(results: Results): void {
        if (this.state !== 'running' || !this.canvasContext || !this.outputCanvas) {
            return;
        }
        this.applyCanvasSize();
        const {width, height} = this.outputCanvas;

        try {
            // Сохраняем оригинальное изображение во временный canvas
            if (this.tempContext && this.tempCanvas) {
                this.tempContext.clearRect(0, 0, this.tempCanvas.width, this.tempCanvas.height);
                this.tempContext.fillStyle = 'gray';
                this.tempContext.fillRect(0, 0, this.tempCanvas.width, this.tempCanvas.height);
                this.tempContext.drawImage(results.image, this.tempCanvasPadding, this.tempCanvasPadding, width, height);

                let blurRadius = this.options.blurRadius;
                // Применяем размытие
                //blurRadius = Random.randomNumber(1, 50);
                this.tempContext.filter = `blur(${blurRadius}px)`;
                this.tempContext.drawImage(this.tempCanvas, 0, 0, this.tempCanvas.width, this.tempCanvas.height);

                //this.tempContext.drawImage(this.tempCanvas, -blurRadius * 1.5, -blurRadius * 1.5, width + blurRadius * 3, height + blurRadius * 3);
                this.tempContext.filter = 'none';
            }

            // Очищаем основной canvas
            this.canvasContext.clearRect(0, 0, width, height);

            // Рисуем размытый фон
            if (this.tempCanvas) {
                this.canvasContext.drawImage(this.tempCanvas, this.tempCanvasPadding, this.tempCanvasPadding, width, height, 0, 0, width, height);
            }

            // Используем маску для вырезания человека
            this.canvasContext.save();

            // Обрезаем по маске (оставляем только человека)
            this.canvasContext.globalCompositeOperation = 'destination-out';
            this.canvasContext.drawImage(results.segmentationMask, 0, 0, width, height);

            // Рисуем оригинального человека поверх размытого фона
            this.canvasContext.globalCompositeOperation = 'destination-over';
            this.canvasContext.drawImage(results.image, 0, 0, width, height);

            this.canvasContext.restore();

        } catch (error) {
            console.error('Ошибка отрисовки кадра:', error);
        }
    }


    /**
     * Остановка эффекта и очистка ресурсов
     */
    async stop(): Promise<void> {
        if (this.state === 'stopping' || this.state === 'idle') {
            return;
        }

        this.setState('stopping');

        // Отменяем анимацию
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
            this.frameId = null;
        }

        // Закрываем сегментацию
        if (this.selfieSegmentation) {
            try {
                await this.selfieSegmentation.close();
            } catch (error) {
                console.warn('Ошибка при закрытии сегментации:', error);
            }
            this.selfieSegmentation = null;
        }

        // Останавливаем видео
        if (this.inputVideo) {
            this.inputVideo.pause();
            this.inputVideo.srcObject = null;
            this.inputVideo = null;
        }

        // Останавливаем выходной stream
        if (this.outputStream) {
            this.outputStream.getTracks().forEach((track: MediaStreamTrack) => {
                track.stop();
            });
            this.outputStream = null;
        }

        // Очищаем canvas
        this.canvasContext = null;
        this.tempContext = null;
        this.outputCanvas = null;
        this.tempCanvas = null;
        this.originalStream = null;

        this.setState('idle');
    }

    /**
     * Изменение уровня размытия
     */
    setBlurRadius(radius: number): void {
        this.options.blurRadius = Math.max(1, Math.min(50, radius));
    }

    /**
     * Изменение модели сегментации
     */
    setModelSelection(model: 0 | 1): void {
        this.options.modelSelection = model;
        if (this.selfieSegmentation) {
            this.selfieSegmentation.setOptions({modelSelection: model});
        }
    }

    /**
     * Изменение FPS
     */
    setFrameRate(frameRate: number): void {
        this.options.frameRate = Math.max(1, Math.min(60, frameRate));
        // Note: Чтобы применить новый FPS, нужно перезапустить эффект
    }

    /**
     * Получение текущих опций
     */
    getOptions(): Required<BackgroundBlurOptions> {
        return {...this.options};
    }

    /**
     * Получение текущего состояния
     */
    getState(): EffectState {
        return this.state;
    }

    /**
     * Получение выходного потока
     */
    getOutputStream(): MediaStream | null {
        return this.outputStream;
    }

    /**
     * Обновление состояния
     */
    private setState(state: EffectState): void {
        if (this.state !== state) {
            this.state = state;
            this.onStateChange?.(state);
        }
    }
}